"use client";

import { useState, useEffect } from "react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/app/components/Badge";
import type React from "react";
import { HiChevronDown } from "react-icons/hi2";
import { Button } from "@/app/components/Button";
import Link from "next/link";

interface PricingInfo {
  metric?: string;
  product?: string;
  type?: string;
  cpu?: string;
  ram?: string;
  disk?: string;
  size?: string;
  price: string;
}

interface ArchitectureInfo {
  architecture: string;
  info: PricingInfo[];
}

interface OSInfo {
  name: string;
  icon: string;
  architectures: ArchitectureInfo[];
}

interface UsagePricingCardProps {
  title?: string;
  icon?: React.ReactNode;
  description?: string;
  osOptions?: OSInfo[];
  info?: PricingInfo[];
  columnHeaders?: [string, string];
  beta?: boolean;
  cta?: {
    text: string;
    href: string;
    variant: "primary" | "secondary" | "tertiary";
  };
}

export function UsagePricingCard({
  title,
  icon,
  description,
  osOptions,
  info,
  columnHeaders,
  beta,
  cta,
}: UsagePricingCardProps) {
  const [selectedOS, setSelectedOS] = useState(
    osOptions ? osOptions[0].name : ""
  );
  const [activeArchIndex, setActiveArchIndex] = useState(0);

  // Reset architecture index when OS changes
  useEffect(() => {
    setActiveArchIndex(0);
  }, [selectedOS]);

  const formatSpecification = (item: PricingInfo) => {
    const parts = [];
    if (item.cpu) parts.push(item.cpu);
    if (item.ram) parts.push(item.ram);
    if (item.disk) parts.push(item.disk);
    return parts.join(", ");
  };

  const renderPricingTable = (data: PricingInfo[]) => (
    <div className="p-5">
      <table className="w-full font-normal">
        <thead>
          <tr className="text-left text-sm">
            <th className="pb-2 font-normal text-text-body-secondary">
              {columnHeaders ? columnHeaders[0] : "Specification"}
            </th>
            <th className="pb-2 font-normal text-text-body-primary text-right">
              {columnHeaders ? columnHeaders[1] : "Price"}
            </th>
          </tr>
        </thead>
        <tbody>
          {data.map((item, index) => (
            <tr key={index} className="text-sm">
              <td className="py-2 pr-4 align-top">
                <div className="text-text-body-secondary">
                  {item.metric ||
                    item.product ||
                    item.size ||
                    formatSpecification(item)}
                </div>
              </td>
              <td className="py-2 text-text-body-primary text-right">
                {item.price}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // Get current OS data with safety check
  const getCurrentOSData = () => {
    const currentOS = osOptions?.find((os) => os.name === selectedOS);
    if (!currentOS) return null;

    const architectures = currentOS.architectures;
    // Ensure activeArchIndex is within bounds
    const safeArchIndex = Math.min(activeArchIndex, architectures.length - 1);

    return {
      os: currentOS,
      architecture: architectures[safeArchIndex],
      architectures,
    };
  };

  return (
    <div className="bg-usage-pricing-gradient border border-border-default rounded-lg">
      {(icon || title || description) && (
        <div className="flex border-b items-start border-dotted border-border-hover gap px-5 py-4">
          <div className="flex-1">
            {title && (
              <div className="relative flex items-center gap-1">
                <div className="mr-3 text-icon-green">{icon}</div>
                <h3 className="text-lg text-text-body-primary font-medium">
                  {title}
                </h3>
                {beta && (
                  <div className="absolute -top-7 -right-8">
                    <Badge
                      title="BETA"
                      className="bg-surface-green-dark border-none text-xs text-white"
                    />
                  </div>
                )}
              </div>
            )}
            {description && (
              <p className="text-sm mt-1">
                <span className="text-text-body-secondary">{description}</span>
                {beta && (
                  <span className="text-text-body-green ml-1">
                    {" "}
                    Subject to change during beta.
                  </span>
                )}
              </p>
            )}
          </div>
        </div>
      )}
      {osOptions ? (
        <div>
          <div className="flex items-center justify-between mt-5 px-5">
            <div className="relative inline-block">
              <select
                value={selectedOS}
                onChange={(e) => setSelectedOS(e.target.value)}
                className="bg-surface-hover border text-text-body-primary border-border-default rounded-sm pl-2 pr-8 py-1 text-sm appearance-none"
              >
                {osOptions.map((os) => (
                  <option key={os.name} value={os.name}>
                    {os.name}
                  </option>
                ))}
              </select>
              <HiChevronDown
                className="absolute right-2 top-1/2 -translate-y-1/2 text-icon-secondary-hover pointer-events-none"
                size={16}
              />
            </div>
            {getCurrentOSData()?.architectures && (
              <Tabs
                defaultValue={String(activeArchIndex)}
                onValueChange={(val) => setActiveArchIndex(Number(val))}
                className="inline-flex"
              >
                <TabsList className="flex justify-center gap-1 bg-surface-hover border border-border-default rounded-md p-1 h-auto">
                  {getCurrentOSData()?.architectures.map((arch, index) => (
                    <TabsTrigger
                      key={arch.architecture}
                      value={String(index)}
                      className="rounded-sm px-3 py-1.5 text-xs font-medium text-text-body-secondary border border-transparent hover:bg-surface-hover transition-all duration-150 data-[state=active]:bg-surface-green-subtle data-[state=active]:text-text-body-green data-[state=active]:border-border-green"
                    >
                      {arch.architecture}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            )}
          </div>
          {getCurrentOSData()?.architecture &&
            renderPricingTable(getCurrentOSData()?.architecture.info || [])}
        </div>
      ) : (
        <div>
          {info && renderPricingTable(info)}
          {cta && (
            <div className="px-5 pb-5">
              <Button
                variant={cta.variant}
                as={Link}
                href={cta.href}
                openInNewTab={true}
                className="w-full"
              >
                {cta.text}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
